import React from 'react';
import { SiteSection, SiteSectionHeader } from '../../components/site';
import type { SolutionEcosystem, SolutionEcosystemCard } from './types';

function EcosystemIllustration({ kind }: { kind: SolutionEcosystemCard['illustration'] }) {
  switch (kind) {
    case 'room':
      return (
        <svg viewBox="0 0 120 72" fill="none" aria-hidden>
          <rect x="8" y="14" width="104" height="48" rx="6" stroke="currentColor" strokeWidth="1.5" />
          <rect x="18" y="24" width="28" height="20" rx="3" stroke="currentColor" strokeWidth="1.25" opacity="0.7" />
          <path d="M58 38h34" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" opacity="0.5" />
          <path d="M58 46h22" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" opacity="0.35" />
          <circle cx="88" cy="28" r="8" stroke="currentColor" strokeWidth="1.25" />
          <path d="M88 24v8M84 28h8" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
        </svg>
      );
    case 'guest':
      return (
        <svg viewBox="0 0 120 72" fill="none" aria-hidden>
          <rect x="38" y="8" width="44" height="56" rx="8" stroke="currentColor" strokeWidth="1.5" />
          <rect x="44" y="16" width="32" height="36" rx="4" stroke="currentColor" strokeWidth="1.25" opacity="0.65" />
          <circle cx="60" cy="58" r="3" fill="currentColor" opacity="0.5" />
          <path d="M50 28h20M50 34h14M50 40h18" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" opacity="0.45" />
        </svg>
      );
    case 'staff':
      return (
        <svg viewBox="0 0 120 72" fill="none" aria-hidden>
          <path d="M24 52V28l36-12 36 12v24" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <rect x="44" y="36" width="32" height="16" rx="3" stroke="currentColor" strokeWidth="1.25" />
          <path d="M52 44h16M52 48h10" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" opacity="0.5" />
          <circle cx="88" cy="22" r="7" stroke="currentColor" strokeWidth="1.25" />
          <path d="M88 19v6M85 22h6" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
        </svg>
      );
    case 'property':
      return (
        <svg viewBox="0 0 120 72" fill="none" aria-hidden>
          <rect x="14" y="22" width="36" height="38" rx="4" stroke="currentColor" strokeWidth="1.5" />
          <rect x="54" y="14" width="28" height="46" rx="4" stroke="currentColor" strokeWidth="1.5" opacity="0.85" />
          <rect x="86" y="28" width="22" height="32" rx="4" stroke="currentColor" strokeWidth="1.5" opacity="0.7" />
          <path d="M22 34h18M22 42h14M62 26h12M62 34h12M94 38h8" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity="0.4" />
        </svg>
      );
    case 'analytics':
      return (
        <svg viewBox="0 0 120 72" fill="none" aria-hidden>
          <path d="M16 56V20M16 56h88" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M28 48l16-14 14 8 24-22" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="28" cy="48" r="3" fill="currentColor" opacity="0.55" />
          <circle cx="44" cy="34" r="3" fill="currentColor" opacity="0.55" />
          <circle cx="58" cy="42" r="3" fill="currentColor" opacity="0.55" />
          <circle cx="82" cy="20" r="3" fill="currentColor" opacity="0.55" />
        </svg>
      );
    default:
      return null;
  }
}

function EcosystemDecor() {
  return (
    <div className="hosp-ecosystem__decor" aria-hidden>
      <svg className="hosp-ecosystem__decor-icon hosp-ecosystem__decor-icon--wifi" viewBox="0 0 24 24" fill="none">
        <path d="M2.5 8.5a14 14 0 0 1 19 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M5.5 11.5a10 10 0 0 1 13 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="12" cy="18" r="1.25" fill="currentColor" />
      </svg>
      <svg className="hosp-ecosystem__decor-icon hosp-ecosystem__decor-icon--cloud" viewBox="0 0 24 24" fill="none">
        <path
          d="M7 18h10a4 4 0 0 0 .4-8 5.5 5.5 0 0 0-10.6 1.8A3.5 3.5 0 0 0 7 18Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
      <span className="hosp-ecosystem__decor-nodes">
        <span />
        <span />
        <span />
        <span />
      </span>
      <svg className="hosp-ecosystem__decor-icon hosp-ecosystem__decor-icon--graph" viewBox="0 0 24 24" fill="none">
        <path d="M4 18V6M4 18h16M8 14l3-4 3 2 4-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <svg className="hosp-ecosystem__decor-icon hosp-ecosystem__decor-icon--device" viewBox="0 0 24 24" fill="none">
        <rect x="7" y="4" width="10" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="17" r="1" fill="currentColor" />
      </svg>
    </div>
  );
}

function JourneyConnector() {
  return (
    <div className="hosp-ecosystem__connector" aria-hidden>
      <svg className="hosp-ecosystem__connector-svg" viewBox="0 0 48 24" preserveAspectRatio="none">
        <line x1="0" y1="12" x2="48" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle className="hosp-ecosystem__connector-pulse" cx="12" cy="12" r="3" fill="currentColor">
          <animateMotion
            dur="2.4s"
            repeatCount="indefinite"
            path="M 4 12 L 44 12"
            keyTimes="0;1"
            calcMode="spline"
            keySplines="0.42 0 0.58 1"
          />
        </circle>
      </svg>
    </div>
  );
}

export const SolutionHospitalityEcosystem: React.FC<{
  ecosystem: SolutionEcosystem;
}> = ({ ecosystem }) => (
  <SiteSection id="ecosystem" className="hosp-ecosystem">
    <EcosystemDecor />
    <SiteSectionHeader
      eyebrow={ecosystem.eyebrow}
      title={ecosystem.title}
      subtitle={ecosystem.subtitle}
      align="center"
    />

    <div className="hosp-ecosystem__journey">
      {ecosystem.cards.map((card, index) => (
        <React.Fragment key={card.id}>
          <article
            className={`hosp-ecosystem__card hosp-ecosystem__card--${index + 1}`}
            style={{ '--card-i': index } as React.CSSProperties}
          >
            <div className="hosp-ecosystem__illus" aria-hidden>
              <EcosystemIllustration kind={card.illustration} />
            </div>
            <div className="hosp-ecosystem__card-head">
              <span className="hosp-ecosystem__emoji" aria-hidden>
                {card.emoji}
              </span>
              <h3 className="hosp-ecosystem__card-title">{card.title}</h3>
            </div>
            <ul className="hosp-ecosystem__list">
              {card.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </article>
          {index < ecosystem.cards.length - 1 ? <JourneyConnector /> : null}
        </React.Fragment>
      ))}
    </div>

    <ul className="hosp-ecosystem__metrics" aria-label="Ecosystem outcomes">
      {ecosystem.metrics.map((metric) => (
        <li key={metric.label} className="hosp-ecosystem__metric">
          <span className="hosp-ecosystem__metric-emoji" aria-hidden>
            {metric.emoji}
          </span>
          <span className="hosp-ecosystem__metric-label">{metric.label}</span>
        </li>
      ))}
    </ul>
  </SiteSection>
);
